import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { subMonths } from 'date-fns';

// Define interfaces
interface LeadSourceData {
    leadArrived: number;
    leadHot: number;
    leadConverted: number;
    leadLost: number;
}

interface LeadConversionResponse {
    status: boolean;
    message: string;
    data: {
        [key: string]: LeadSourceData;
    };
}

interface LeadConversionReportState {
    loading: boolean;
    error: string | null;
    data: {
        [key: string]: LeadSourceData;
    };
    startDate: Date | null;
    endDate: Date | null;
}

// Initial state
const initialState: LeadConversionReportState = {
    loading: false,
    error: null,
    data: {},
    startDate: subMonths(new Date(), 1),
    endDate: new Date(),
};

// Async thunk for fetching lead conversion report
export const fetchLeadConversionReport = createAsyncThunk(
    'leadConversionReport/fetch',
    async (payload: { startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response =  await api.getLeadConversionData(
                payload.startDate,
                payload.endDate
            ) as LeadConversionResponse;
            
            if (response.status) {
                return response.data;
            } else {
                return rejectWithValue(response.message || 'Failed to fetch lead conversion report');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch lead conversion report');
        }
    }
);

// Create slice
const leadConversionReportSlice = createSlice({
    name: 'leadConversionReport',
    initialState,
    reducers: {
        setStartDate: (state, action: PayloadAction<Date | null>) => {
            state.startDate = action.payload;
        },
        setEndDate: (state, action: PayloadAction<Date | null>) => {
            state.endDate = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetLeadConversionReport: (state) => {
            state.data = {};
            state.error = null;
            state.loading = false;
            state.startDate = subMonths(new Date(), 1);
            state.endDate = new Date();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLeadConversionReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLeadConversionReport.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchLeadConversionReport.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setStartDate, setEndDate, clearError, resetLeadConversionReport } = leadConversionReportSlice.actions;

export default leadConversionReportSlice.reducer;